import { z } from 'zod';
import { JwtSignInPayload } from '../../JwtAuthProvider';

/**
 * Form validation schema using Zod for type-safe validation
 */
export const signInFormSchema = z.object({
  username: z
    .string()
    .email('You must enter a valid email')
    .min(1, 'You must enter an email'),
  password: z.string().min(4, 'Please enter your password.'),
  otp: z.string().optional(),
  remember_me_duration: z.number().optional(),
  recovery_code: z.string().optional(),
});

/**
 * Extended form type that includes OTP and recovery options
 */
export type SignInFormType = JwtSignInPayload & {
  remember_me_duration?: number;
  otp?: string;
  recovery_code?: string;
  method?: string;  // For specifying alternative 2FA method
};

/**
 * Multi-factor authentication types supported by the system
 */
export type MfaType = 'totp' | 'email' | 'otp' | 'recovery';

/**
 * Alternative 2FA method information
 */
export interface AlternativeMethod {
  method: string;
  display_name: string;
}

/**
 * API error structure for handling authentication errors
 */
export interface ApiError extends Error {
  data?: {
    detail?: string;
    error?: string;
    '2fa_type'?: string;
    identifier?: string;
    alternative_methods?: AlternativeMethod[];
  };
}

/**
 * Default form values for initialization
 */
export const defaultSignInFormValues: SignInFormType = {
  username: '',
  password: '',
  otp: '',
  remember_me_duration: 0,
  recovery_code: '',
};

/**
 * Remember me duration options for 2FA
 */
export const REMEMBER_ME_OPTIONS = [
  { value: 0, label: "Don't remember" },
  { value: 1, label: '1 Day' },
  { value: 7, label: '7 Days' },
  { value: 31, label: '31 Days' },
] as const;